import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCms } from "./context/CmsContext";
import logoImg from "./PartyBoss-Logo5.svg";
import SocialMediaIcons, { normalizeSocialUrl } from "./components/SocialMediaIcons";
import { homeContentDefaults } from "./cms/content/home.content";
import { LG_UP_QUERY, useMediaQuery } from "./hooks/useMediaQuery";
import "./partyBossTheme.css";

/** Slim top strip — similar layout to luxury venue sites (inline phone · email · chat). */
function formatHeaderPhone(digits) {
  const raw = String(digits || "").replace(/\D/g, "");
  const national =
    raw.startsWith("91") && raw.length >= 12 ? raw.slice(2, 12) : raw.slice(-10);
  if (national.length === 10) {
    return { tel: `+91${national}`, display: `+91 ${national}` };
  }
  return { tel: raw ? `+${raw}` : "+91", display: raw ? `+${raw}` : "+91" };
}

function ContactTopBar({ waHref, phoneDigits, socialLinks }) {
  const { tel, display } = formatHeaderPhone(phoneDigits || "917780291217");
  const mergedSocial = {
    ...homeContentDefaults.socialLinks,
    ...(socialLinks || {}),
  };
  const hasSocialLinks =
    Boolean(normalizeSocialUrl(mergedSocial.instagram)) ||
    Boolean(normalizeSocialUrl(mergedSocial.youtube)) ||
    Boolean(normalizeSocialUrl(mergedSocial.facebook));
  return (
    <div className="party-boss-nav-topbar" aria-label="Contact Party Boss">
      <div className="party-boss-nav-container party-boss-nav-topbar-stack">
        <div className="party-boss-nav-topbar-panel">
          <div className="party-boss-nav-topbar-inner">
            <div className="party-boss-nav-topbar-contact">
              <span className="party-boss-nav-topbar-loc">
                <i className="bi bi-geo-alt-fill" aria-hidden />
                <span>Miyapur, Hyderabad</span>
              </span>
              <span className="party-boss-nav-topbar-sep" aria-hidden />
              <a href={`tel:${tel}`} className="party-boss-nav-topbar-link">
                <i className="bi bi-telephone-fill" aria-hidden />
                <span className="party-boss-nav-topbar-link-text">{display}</span>
              </a>
              <span className="party-boss-nav-topbar-sep" aria-hidden />
              <a href="mailto:info@partyboss.in" className="party-boss-nav-topbar-link">
                <i className="bi bi-envelope-fill" aria-hidden />
                <span className="party-boss-nav-topbar-link-text">info@partyboss.in</span>
              </a>
              <span className="party-boss-nav-topbar-sep party-boss-nav-topbar-sep--hide-sm" aria-hidden />
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="party-boss-nav-topbar-link party-boss-nav-topbar-link--wa"
              >
                <i className="bi bi-whatsapp" aria-hidden />
                <span className="party-boss-nav-topbar-link-text">WhatsApp</span>
              </a>
              {hasSocialLinks ? (
                <>
                  <span
                    className="party-boss-nav-topbar-sep party-boss-nav-topbar-sep--social"
                    aria-hidden
                  />
                  <SocialMediaIcons
                    social={mergedSocial}
                    variant="topbar"
                    className="party-boss-social--topbar-inline"
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavLinks({ navItems, className, variant = "flat", onNavigate }) {
  const trackClass =
    variant === "pill"
      ? `party-boss-nav-pill-track ${className || ""}`
      : `party-boss-nav-track-flat ${className || ""}`;
  const linkClass =
    variant === "pill" ? "party-boss-nav-link" : "party-boss-nav-link party-boss-nav-link--flat";

  return (
    <div className={trackClass}>
      {navItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={linkClass}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}

const NavBar = ({ maintenanceMode = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isLgUp = useMediaQuery(LG_UP_QUERY);
  const navigate = useNavigate();
  const { cms } = useCms();
  const waHref = useMemo(
    () => `https://wa.me/${cms.home?.whatsappNumber || "917780291217"}`,
    [cms.home?.whatsappNumber]
  );

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (isLgUp) setMenuOpen(false);
  }, [isLgUp]);

  useEffect(() => {
    if (!menuOpen || isLgUp) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen, isLgUp]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, closeMenu]);

  if (maintenanceMode) {
    return (
      <div id="partyboss-nav" className="relative">
        <header className="party-boss-nav-header">
          <ContactTopBar
            waHref={waHref}
            phoneDigits={cms.home?.whatsappNumber}
            socialLinks={cms.home?.socialLinks}
          />
          <div className="party-boss-nav-main">
            <div className="party-boss-nav-container party-boss-nav-main-stack">
              <div className="party-boss-nav-white-panel party-boss-nav-white-panel--no-promo">
                <div className="party-boss-nav-main-inner flex w-full min-h-[4.25rem] items-center justify-center py-2">
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="party-boss-nav-brand party-boss-nav-brand--overlay flex cursor-pointer border-0 bg-transparent p-0"
                    aria-label="Party Boss — Home"
                  >
                    <span className="party-boss-nav-logo-inner">
                      <img
                        src={logoImg}
                        alt="Party Boss"
                        className="party-boss-nav-logo-img"
                        decoding="async"
                      />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/celebrations", label: "Celebrations" },
    { to: "/food", label: "Food" },
    { to: "/gallery", label: "Gallery" },
    { to: "/my-booking", label: "My Booking" },
    { to: "/contact", label: "Contact" },
  ];

  const promo = cms.home?.promoBanner;
  const showPromoBanner =
    promo?.enabled !== false && Boolean(promo?.text && String(promo.text).trim());

  return (
    <div id="partyboss-nav" className="relative">
      <header className="party-boss-nav-header">
        <ContactTopBar
          waHref={waHref}
          phoneDigits={cms.home?.whatsappNumber}
          socialLinks={cms.home?.socialLinks}
        />

        <div className="party-boss-nav-main">
          <div className="party-boss-nav-container party-boss-nav-main-stack">
            <div
              className={`party-boss-nav-white-panel${showPromoBanner ? "" : " party-boss-nav-white-panel--no-promo"}`}
            >
              <button
                type="button"
                onClick={() => {
                  navigate("/");
                  setMenuOpen(false);
                }}
                className="party-boss-nav-brand party-boss-nav-brand--overlay flex cursor-pointer border-0 bg-transparent p-0"
                aria-label="Party Boss — Home"
              >
                <span className="party-boss-nav-logo-inner">
                  <img
                    src={logoImg}
                    alt="Party Boss"
                    className="party-boss-nav-logo-img"
                    decoding="async"
                  />
                </span>
              </button>

              <div className="party-boss-nav-main-inner">
                <div className="party-boss-nav-logo-spacer" aria-hidden="true" />

                <nav
                  className="party-boss-nav-desktop-nav hidden min-w-0 flex-1 lg:flex"
                  aria-label="Primary"
                >
                  <NavLinks navItems={navItems} variant="flat" />
                </nav>

                <div className="party-boss-nav-cta-group flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/booknow")}
                    className="party-boss-nav-cta inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#ffd166] to-[#ff8fab] px-3 py-2 text-[11px] font-black uppercase tracking-[0.06em] text-[#120817] shadow-md sm:px-4 sm:py-2.5 sm:text-[13px]"
                  >
                    <i className="bi bi-calendar2-event text-sm sm:text-base" aria-hidden />
                    Book now
                  </button>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="party-boss-nav-icon-btn party-boss-nav-icon-btn--menu grid h-9 w-9 shrink-0 place-items-center rounded-lg border text-lg lg:hidden"
                    aria-expanded={menuOpen}
                    aria-controls="partyboss-nav-menu"
                    aria-label={menuOpen ? "Close menu" : "Open menu"}
                  >
                    <i className={`bi ${menuOpen ? "bi-x-lg" : "bi-list"}`} aria-hidden />
                  </button>
                </div>
              </div>

              {showPromoBanner ? (
                <div
                  className="party-boss-nav-promo-banner"
                  role="region"
                  aria-label="Promotional offer"
                >
                  <div className="party-boss-nav-promo-marquee-wrap">
                    <div className="party-boss-nav-promo-marquee-track">
                      <span className="party-boss-nav-promo-marquee-item">{promo.text}</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {menuOpen && !isLgUp ? (
          <button
            type="button"
            className="party-boss-nav-backdrop"
            aria-label="Close navigation menu"
            onClick={closeMenu}
          />
        ) : null}

        <nav
          id="partyboss-nav-menu"
          className={`party-boss-nav-dropdown party-boss-nav-mobile-panel flex-col ${
            menuOpen ? "party-boss-nav-mobile-panel--open" : ""
          }`}
          aria-label="Primary navigation"
          aria-hidden={!menuOpen || isLgUp}
        >
          <div className="party-boss-nav-drawer-head flex items-center justify-between border-b border-[rgba(124,58,237,0.12)] px-4 py-3">
            <span className="text-sm font-black uppercase tracking-[0.12em] text-[#422850]">
              Menu
            </span>
            <button
              type="button"
              className="party-boss-nav-icon-btn party-boss-nav-icon-btn--drawer-close grid h-10 w-10 shrink-0 place-items-center rounded-lg border text-lg"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <i className="bi bi-x-lg" aria-hidden />
            </button>
          </div>
          <div className="party-boss-nav-container flex-1 overflow-y-auto py-2">
            <NavLinks
              navItems={navItems}
              onNavigate={closeMenu}
              variant="flat"
              className="party-boss-nav-track-flat--stack"
            />
            <SocialMediaIcons social={cms.home?.socialLinks} variant="mobile" />
          </div>
        </nav>
      </header>
    </div>
  );
};

export default NavBar;
